import { useState, type JSX } from 'react';

import { PermissionRoles } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { useQueryClient } from '@tanstack/react-query';
import { components, GroupBase, MultiValueGenericProps } from 'react-select';

import { CreatableMultiSelect } from '@/components/ui/multi-select';
import { useToast } from '@/components/ui/toaster';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { userKeys } from '@/queries/users';
import { ApiService } from '@/services/api-v2';

import { Utils } from '../../legacy/utils';

export const InviteUsers = () => {
  const [saving, setSaving] = useState<boolean>(false);
  const [inputEmail, setInputEmail] = useState<string>('');
  const [emails, setEmails] = useState<string[]>([]);
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const toast = useToast();

  const MAX_EMAILS = 100;

  const handleSubmit = () => {
    if (!emails.length) return;

    setSaving(true);
    const uniqueEmails = [...new Set(emails)];
    ApiService.AddUsers({ emails: uniqueEmails, role: PermissionRoles.GearbeltMember })
      .then(() => {
        setEmails([]);
        toast.success(`${uniqueEmails.length} Contributor${uniqueEmails.length > 1 ? 's' : ''} added successfully.`);
        queryClient.invalidateQueries({ queryKey: userKeys.all });
      })
      .catch(() => {
        toast.error('Failed to invite users');
      })
      .finally(() => setSaving(false));
  };

  const handleOnCreate = async (rawEmail: string) => {
    if (emails.length >= MAX_EMAILS) return;
    if (!rawEmail) return;
    const email = rawEmail.toLowerCase();
    setEmails([...emails, email]);
    setInputEmail('');

    if (emails.includes(email)) return;

    if (!Utils.ValidateEmail(email)) {
      setInvalidEmails([...invalidEmails, email]);
    } else {
      const filters = { filters: { search: email } };
      const { data } = await (await ApiService.GetUsers({ limit: 1, ...filters })).json();
      if (data.length) {
        setInvalidEmails([...invalidEmails, email]);
      }
    }
  };

  function getInvalidEmailsMessage() {
    if (emails.length >= MAX_EMAILS) return 'You can only invite up to 100 users at a time.';
    if (!invalidEmails.length) return;

    const hasInvalidFormat = invalidEmails.some((email) => !Utils.ValidateEmail(email));
    const hasExistingUsers = invalidEmails.some((email) => Utils.ValidateEmail(email));

    if (hasInvalidFormat && hasExistingUsers) return 'You have multiple invalid emails. Hover over the emails in red to see the issue.';
    if (hasExistingUsers) return 'User already has access';
    return 'Please enter a valid email address format name@example.com';
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex w-full flex-col gap-1">
        <div className="flex w-full flex-1 flex-row">
          <div className="flex min-w-[469px] flex-col gap-2 overflow-hidden">
            <CreatableMultiSelect
              className="h-full max-w-full gap-2"
              classNamePrefix="react-select"
              isMulti
              isDisabled={saving}
              placeholder="Type email address..."
              noOptionsMessage={() => null}
              formatCreateLabel={(e: string) => <span>{e}</span>}
              isValidNewOption={(newEmail: string) => Utils.ValidateEmail(newEmail)}
              onInputChange={(email) => {
                setInputEmail(email);
              }}
              inputValue={inputEmail}
              onKeyDown={(e) => {
                if (e.key === ',') {
                  e.preventDefault();
                  handleOnCreate(inputEmail);
                }
              }}
              onCreateOption={handleOnCreate}
              onChange={(emails) => {
                setEmails(emails?.map((email) => email.value));
                setInvalidEmails(invalidEmails?.filter((email) => emails.find((e) => e.value === email)).map((email) => email) || []);
              }}
              value={emails?.map((email) => ({ value: email, label: email }))}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderTopRightRadius: '0px',
                  borderBottomRightRadius: '0px',
                }),
                input: (baseStyles) => ({
                  ...baseStyles,
                  cursor: 'text',
                }),
                dropdownIndicator: (baseStyles) => ({
                  ...baseStyles,
                  display: 'none',
                }),
                clearIndicator: (baseStyles) => ({
                  ...baseStyles,
                  display: 'none',
                }),
                indicatorSeparator: (baseStyles) => ({
                  ...baseStyles,
                  display: 'none',
                }),
              }}
              components={{
                MultiValueLabel: ({
                  data,
                  ...props
                }: MultiValueGenericProps<{ value: string; label: string }, true, GroupBase<{ value: string; label: string }>>): JSX.Element => {
                  return (
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger className="cursor-default">
                          <components.MultiValueLabel data={data} {...props} />
                        </TooltipTrigger>
                        {invalidEmails.includes(data.value) && (
                          <TooltipPortal>
                            <TooltipContent side="bottom" sideOffset={0} className="bg-default-text border-none p-1 text-xs text-white">
                              <TooltipArrow className="fill-default-text" />
                              {Utils.ValidateEmail(data.value) ? 'User already exists' : 'Please enter a valid email address format name@example.com'}
                            </TooltipContent>
                          </TooltipPortal>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  );
                },
              }}
            />
          </div>
          <Button
            size="lg"
            variant="success"
            disabled={saving || !emails.length || !!invalidEmails.length}
            onClick={handleSubmit}
            className="flex w-fit items-center gap-1 self-stretch rounded-l-none px-4 font-medium">
            {saving ? 'Inviting ...' : 'Invite Users'}
          </Button>
        </div>
        {(invalidEmails.length > 0 || emails.length >= MAX_EMAILS) && <div className="text-default-red text-xs font-normal">{getInvalidEmailsMessage()}</div>}
        <div className="text-secondary-text text-xs font-normal">
          You can add multiple users by entering email addresses separated by commas or press 'Enter' after each one.
        </div>
      </div>
    </div>
  );
};
